/* eslint-disable no-param-reassign */
import elementsTypes from "utils/elementsTypes";
import { v4 as uuidv4 } from "uuid";

const enhanceContents = async ({ contents, getPages, removeEmail = false, removeSubpagesContents = false }) => {
  const pagesResponses = {};
  const flatContents = [];

  const getPage = (id) => {
    if (!pagesResponses[id] || !pagesResponses[id].promise) {
      const response = {};
      // Creates à promise for each waiting page
      response.promise = new Promise((resolve, reject) => {
        response.resolve = resolve;
        response.reject = reject;
      });
      pagesResponses[id] = response;
    }
    return pagesResponses[id].promise;
  };

  const addPages = (currentContents, goDeep = false) => {
    return Promise.all(
      (currentContents || []).map(async (content) => {
        const newContent = content;
        if ([elementsTypes.PAGE, elementsTypes.SUBPAGE].includes(content.type)) {
          if (typeof content.value === "string" && content.value) {
            // enhance and replace id by page data
            const page = await getPage(content.value);
            if (!page) {
              return null;
            }
            newContent.value = page;
            if (typeof content.value === "object" && content.type === elementsTypes.SUBPAGE && goDeep) {
              // enhance in depth (only deep 2)
              newContent.value = {
                ...newContent.value,
                contents: await addPages(content.value.contents),
              };
            } else if (removeSubpagesContents) {
              newContent.value = {
                ...newContent.value,
                contents: [],
              };
            }
          }
        }
        if (content.children && content.children.length) {
          newContent.children = await addPages(content.children, goDeep);
        }
        flatContents.push(newContent);
        return newContent;
      }),
    ).then((newContents) => newContents.filter((content) => content !== null));
  };

  const promise = addPages(contents, true);
  // Resolve all waitigin pages in one request
  const resolve = () => {
    const ids = Object.keys(pagesResponses);
    if (ids.length) {
      const perPage = 10;
      const promises = [];
      let i = 0;
      while (perPage * i < ids.length) {
        const groupIds = ids.slice(perPage * i, perPage * i + perPage);
        promises.push(getPages(groupIds));
        i += 1;
      }
      Promise.all(promises).then((responses) => {
        const pages = responses.flat();
        ids.forEach((pageId) => {
          const page = pages?.find((p) => p.page && p.page.id === pageId);
          if (page) {
            pagesResponses[pageId].resolve(page);
          } else {
            pagesResponses[pageId].resolve(null);
          }
          delete pagesResponses[pageId];
        });
        // Create a new thread to be executed after previous promise resolution (await getPage from addPages)
        setTimeout(() => {
          resolve();
        }, 0);
      });
    }
  };
  resolve();
  return promise.then((newContents) => {
    const transformContents = (contentsToTransform) => {
      if (Array.isArray(contentsToTransform)) {
        contentsToTransform.forEach((content) => {
          if ([elementsTypes.PAGE, elementsTypes.SUBPAGE].includes(content.type) && typeof content.value === "object") {
            transformContents(content.value.contents);
          } else if (content.type === elementsTypes.CONTENTS_GROUP && content.children) {
            content.children = content.children
              .map((child) => {
                if (child.key === "contents") {
                  const relatedContent = contentsToTransform.find((c) => c.id === child.value);
                  if (relatedContent) {
                    return {
                      ...relatedContent,
                      value: child.value,
                      id: child.id,
                      key: "contents",
                    };
                  }
                  return null;
                }
                return child;
              })
              .filter((c) => c !== null);
          } else if (content.type === elementsTypes.MAILTO && removeEmail) {
            content.children = content.children.filter((child) => child.key !== "recipient");
          } else if (content.type === elementsTypes.CONTACT_FORM_SUBJECT_LIST && removeEmail) {
            content.children.forEach((c) => {
              if (c && Array.isArray(c.children)) {
                c.children = c.children.map((child) => {
                  if (child.key === "recipient") {
                    child.value = child.value !== "";
                  }
                  return child;
                });
              }
            });
          } else if (content.type === elementsTypes.STEP) {
            content.type = elementsTypes.STEP_V2;
            content.children = content.children.map((child) => {
              if (child.key === "content") {
                return {
                  id: uuidv4(),
                  type: elementsTypes.STEP_V2_CONTENT,
                  value: "",
                  key: "content",
                  children: [
                    {
                      ...child,
                      key: "detailsElements",
                    },
                  ],
                };
              }
              return child;
            });
          }
        });
      }
    };
    transformContents(flatContents);
    return newContents;
  });
};

export default enhanceContents;
